/* eslint-disable react/no-unescaped-entities */

import { motion } from "framer-motion";
import { MapPin, CalendarCheck, Users, ArrowRight, Sparkles, Flame } from "lucide-react";
import { SESSIONS } from "@/lib/content";
import { fbq } from "@/lib/tracking";
import { iclosedUrl } from "@/lib/booking";

// Génère un mois fluide pour le bandeau d'urgence
function getMonthName() {
  const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  return months[new Date().getMonth()];
}

export default function Sessions({ onCtaClick }) {
  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-[#080808] border-y border-zinc-900" id="sessions" data-testid="sessions-section">
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 mb-12 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#FFD60A]" />
              <span className="font-mono text-xs tracking-[0.4em] text-[#FFD60A] uppercase">Coaching présentiel</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95]">
              4 sessions par mois <br className="hidden md:block" />
              <span className="text-[#FFD60A]">aux dates qui vous arrangent</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-zinc-400 leading-relaxed">
              Nous bloquons <span className="text-white font-semibold">4 créneaux de formation</span> chaque mois dans notre centre principal de Béziers. Les dates sont <span className="text-white font-semibold">ajustées avec vous</span> selon vos disponibilités pro et personnelles.
            </p>
          </div>
        </div>

        {/* Urgency banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-6 flex items-center gap-3 px-5 py-3 bg-red-500/10 border border-red-500/40 text-red-300"
          data-testid="sessions-urgency-banner"
        >
          <Flame size={16} className="text-red-400 animate-pulse-slow shrink-0" />
          <span className="text-sm font-semibold">
            Plus que <span className="text-white font-bold">{Math.max(1, 16 - new Date().getDate() % 12)} places disponibles</span> pour {getMonthName()}
          </span>
        </motion.div>

        {/* Highlight banner */}
        <div className="mb-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-[#FFD60A]/40 bg-[#FFD60A]/[0.06] px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFD60A] flex items-center justify-center shrink-0">
              <Sparkles size={18} className="text-black" />
            </div>
            <div>
              <div className="font-display text-lg text-white uppercase tracking-wide leading-tight">Formation 100% personnalisée</div>
              <div className="text-sm text-zinc-400 mt-0.5">3 jours intensifs · max 4 stagiaires par session · planning sur-mesure</div>
            </div>
          </div>
          <button
            onClick={() => onCtaClick("call_booking", { campaign: "cta-sessions-banner" })}
            className="px-5 py-3 bg-[#FFD60A] text-black font-bold uppercase text-xs tracking-widest hover:bg-yellow-300 transition-all whitespace-nowrap"
            data-testid="sessions-availability-cta"
          >
            Vérifier mes disponibilités
          </button>
        </div>

        {/* 4 slots */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
          {SESSIONS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-[#0a0a0a] p-6 flex flex-col group hover:bg-[#111] transition-colors"
              data-testid={`session-card-${i}`}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-zinc-400">
                  <MapPin size={12} className="text-[#FFD60A]" />
                  <span className="font-mono text-[10px] uppercase tracking-widest">{s.city}</span>
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1 border border-[#FFD60A]/40 text-[#FFD60A]">
                  <Users size={10} />
                  <span className="font-mono text-[10px] uppercase tracking-widest">{s.places} places</span>
                </div>
              </div>

              <div className="flex items-start gap-3 mb-1">
                <CalendarCheck size={20} className="text-[#FFD60A] mt-1" strokeWidth={1.5} />
                <div>
                  <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Créneau {i + 1}</div>
                  <div className="font-display text-2xl text-white uppercase leading-none mt-1">{s.slot}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-zinc-500 leading-relaxed">
                Date convenue avec vous lors de la prise de contact.
              </div>

              <div className="mt-auto pt-6">
                <a
                  href={iclosedUrl(`cta-session-slot-${i + 1}`)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => fbq.schedule({ content_name: "Session iClosed", content_category: `${s.city} - ${s.slot}` })}
                  className="w-full flex items-center justify-between gap-2 py-2.5 px-3 bg-[#FFD60A] text-black font-bold uppercase text-[11px] tracking-widest hover:bg-yellow-300 transition-all group/btn"
                  data-testid="session-book-button"
                >
                  Réserver
                  <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-6 text-center text-xs text-zinc-500 font-mono uppercase tracking-widest">
          Sessions ouvertes toute l'année · planning défini ensemble
        </p>
      </div>
    </section>
  );
}
